import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Select, InputNumber, Checkbox, Row, Col, Segmented } from "antd";

const AddFrameModal = ({ visible, onClose, onAddFrame, players, isEightBall, isTeamMatch, team1Players, team2Players, team1Name, team2Name }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedTeam(null);
      // Set default values for non-8-Ball matches
      if (!isEightBall) {
        form.setFieldsValue({
          loser_remaining_balls: 0,
          eight_ball_flag: false,
          won_on_break: false,
          winner_fouls: 0,
          loser_fouls: 0
        });
      } else {
        form.setFieldsValue({
          won_on_break: false,
          winner_fouls: 0,
          loser_fouls: 0
        });
      }
    }
  }, [visible, form, isEightBall]);

  // Spieler-Optionen für Segmented
  const playerOptions = players?.map(player => ({
    label: player.name,
    value: player.id
  })) || [];

  // Team-Optionen für Segmented (bei Team-Match)
  const teamOptions = isTeamMatch ? [
    { label: team1Name || 'Team 1', value: 1 },
    { label: team2Name || 'Team 2', value: 2 }
  ] : [];

  // Gefilterte Spieler basierend auf ausgewähltem Team
  const getWinnerPlayerOptions = () => {
    if (!isTeamMatch) return playerOptions;
    if (selectedTeam === 1) return team1Players?.map(p => ({ label: p.name, value: p.id })) || [];
    if (selectedTeam === 2) return team2Players?.map(p => ({ label: p.name, value: p.id })) || [];
    return [];
  };

  const handleTeamChange = (value) => {
    setSelectedTeam(value);
    // Reset winner wenn Team wechselt
    form.setFieldsValue({ winner: undefined });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      await onAddFrame(values);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleRemainingBallsChange = (value) => {
    if (value === 0) {
      form.setFieldsValue({ eight_ball_flag: true });
    } else {
      form.setFieldsValue({ eight_ball_flag: false });
    }
  };

  const handleEightBallChange = (checked) => {
    if (checked) {
      form.setFieldsValue({ loser_remaining_balls: 0 });
    }
  };

  return (
    <Modal
      title="Frame hinzufügen"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Abbrechen
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Hinzufügen
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        {/* Team-Match: Erst Team auswählen */}
        {isTeamMatch && (
          <Form.Item
            label="Gewinnendes Team"
            required
          >
            <Segmented
              options={teamOptions}
              value={selectedTeam}
              onChange={handleTeamChange}
              block
              size="large"
            />
          </Form.Item>
        )}

        {/* Gewinner auswählen - Segmented für 2 Spieler, Select für mehr */}
        <Form.Item
          name="winner"
          label={isTeamMatch ? "Gewinner (Spieler)" : "Gewinner"}
          rules={[{ required: true, message: "Bitte den Gewinner auswählen!" }]}
        >
          {!isTeamMatch && playerOptions.length === 2 ? (
            <Segmented
              options={playerOptions}
              block
              size="large"
            />
          ) : isTeamMatch && selectedTeam ? (
            <Segmented
              options={getWinnerPlayerOptions()}
              block
              size="large"
            />
          ) : (
            <Select placeholder="Gewinner auswählen" disabled={isTeamMatch && !selectedTeam}>
              {(isTeamMatch ? getWinnerPlayerOptions() : playerOptions).map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        {/* Feature #24: Break-Tracking - Wer hatte das Break */}
        <Form.Item
          name="breaking_player"
          label="Wer hatte das Break?"
          rules={[{ required: false }]}
        >
          {playerOptions.length <= 4 ? (
            <Segmented
              options={[{ label: '-', value: null }, ...playerOptions]}
              block
            />
          ) : (
            <Select placeholder="Spieler mit Break auswählen" allowClear>
              {players.map((player) => (
                <Select.Option key={`break-${player.id}`} value={player.id}>
                  {player.name}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        {/* Feature #23: Foul-Tracking - Nebeneinander für Platzersparnis */}
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              name="winner_fouls"
              label="Gewinner Fouls"
              rules={[{ required: false }]}
            >
              <InputNumber
                min={0}
                max={20}
                placeholder="Gewinner"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="loser_fouls"
              label="Verlierer Fouls"
              rules={[{ required: false }]}
            >
              <InputNumber
                min={0}
                max={20}
                placeholder="Verlierer"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Break & Run Flag */}
        <Form.Item name="won_on_break" valuePropName="checked">
          <Checkbox>
            Break & Run (mit Break gewonnen)
          </Checkbox>
        </Form.Item>

        {/* 8-Ball Flag (nur für 8-Ball) */}
        {isEightBall && (
          <Form.Item name="eight_ball_flag" valuePropName="checked">
            <Checkbox onChange={(e) => handleEightBallChange(e.target.checked)}>
              8 ist übrig
            </Checkbox>
          </Form.Item>
        )}

        {/* Verbleibende Bälle */}
        {isEightBall && (
          <Form.Item
            name="loser_remaining_balls"
            label="Verbleibende Bälle"
            rules={[{ required: true, message: "Bitte die verbleibenden Bälle eingeben!" }]}
          >
            <InputNumber
              value={0}
              min={0}
              max={7}
              placeholder="Anzahl der verbleibenden Bälle"
              onChange={handleRemainingBallsChange}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddFrameModal;
